import {
  AfterViewInit,
  Component, computed,
  ElementRef,
  input,
  model,
  OnChanges,
  OnInit,
  output, SimpleChanges,
  viewChild,
} from '@angular/core'
import { acquireFocusDelay } from './idr-filter-value.component'
import { FilterParams } from '../shared/utilities/http-params'
import { FormsModule } from '@angular/forms'

const parseNumber = (rawValue: string): number | undefined => {
  if (rawValue) { // Number('') is zero... yuck
    const value = Number(rawValue.trim())
    return Number.isFinite(value) ? value : NaN
  }
  return undefined
}

@Component({
  selector: 'app-idr-number-range',
  imports: [FormsModule],
  template: `
    <input
        #min
        class='input-minmax form-control'
        [placeholder]="minPlaceholder()"
        [(ngModel)]="minModel"
        (blur)="onBlur()"
    />
    @if (maxVisible()) {
      <input
          class='input-minmax form-control'
          placeholder='max'
          [(ngModel)]="maxModel"
          (blur)="onBlur()"
      />
    }
  `,
  styles: `
    :host {
      display: flex;
      column-gap: 5px;
    }
    .input-minmax {
      flex-basis: 0;
      flex-grow: 1;
      font-size: 16px;
      margin-top: 0;
      padding-top: 6px;
      padding-bottom: 6px;
      text-align: right;
    }
  `,
})
export class IdrNumberRangeFilterComponent implements OnInit, OnChanges, AfterViewInit {
  readonly row = input.required<FilterParams>()
  readonly grabFocus = input.required<boolean>()

  readonly rowUpdate = output<FilterParams>()

  protected readonly minModel = model<string>('')
  protected readonly maxModel = model<string>('')
  private readonly minRef = viewChild.required<ElementRef<HTMLInputElement>>('min')

  protected stringValue = computed(() => this.row().values[0].value!)

  protected operator = computed(() => this.row().values[0].operation!)

  protected maxVisible = computed(() => ['between=', '>=', '<='].includes(this.operator()))

  protected minPlaceholder = computed(() => this.maxVisible() ? 'min' : '')

  ngOnInit() {
    this.minModel.subscribe(() => this.onModelChange())
    this.maxModel.subscribe(() => this.onModelChange())
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['row']) {
      this.refreshModels()
    }
  }

  ngAfterViewInit(): void {
    if (this.grabFocus()) {
      window.setTimeout(() => this.acquireFocus(), acquireFocusDelay)
    }
  }

  protected onBlur() {
    window.setTimeout(() => this.refreshModels(), 0)
  }

  private isValid(min: number | undefined, max: number | undefined): boolean {
    if (min === undefined) {
      return max === undefined || Number.isFinite(max)
    } else if (max === undefined) {
      return Number.isFinite(min)
    } else {
      return min <= max
    }
  }

  private parseModels() {
    const min = parseNumber(this.minModel())
    const max = parseNumber(this.maxModel())
    return this.isValid(min, max) ? { min, max } : undefined
  }

  private onModelChange() {
    const minMax = this.parseModels()
    if (minMax) {
      const { min, max } = minMax

      const getOperator = () => {
        const currentOperator = this.operator()
        if (currentOperator === '==' || currentOperator === '!=') {
          return currentOperator
        } else if (typeof min === 'number') {
          return typeof max === 'number' ? 'between=' : '>='
        } else if (typeof max === 'number') {
          return '<='
        } else {
          return 'between='
        }
      }

      const operation = getOperator()
      const value = [min, max].filter(n => n !== undefined).join(',')

      if (operation !== this.operator() || value !== this.stringValue()) {
        this.rowUpdate.emit({
          ...this.row(),
          values: [{ operation, value }],
        })
      }
    }
  }

  private acquireFocus() {
    this.minRef().nativeElement.focus()
  }

  protected refreshModels() {
    const { value, operation } = this.row().values[0]

    const update = (minString: string, maxString: string) => {
      this.minModel.set(minString)
      this.maxModel.set(maxString)
    }

    if (operation === 'between=') {
      const [minRaw, maxRaw] = value.split(',')
      update(minRaw, maxRaw ?? '')
    } else if (operation === '<=') {
      update('', value)
    } else {
      update(value, '')
    }
  }
}
